export type Ok<T> = { kind: 'ok'; value: T };
export type Err<E> = { kind: 'err'; error: E };
export type Result<T, E = Error> = Ok<T> | Err<E>;

export function ok<T>(value: T): Ok<T> {
  return { kind: 'ok', value };
}

export function err<E>(error: E): Err<E> {
  return { kind: 'err', error };
}

export function isOk<T, E>(r: Result<T, E>): r is Ok<T> {
  return r.kind === 'ok';
}

export function isErr<T, E>(r: Result<T, E>): r is Err<E> {
  return r.kind === 'err';
}

export function map<T, U, E>(r: Result<T, E>, fn: (v: T) => U): Result<U, E> {
  return isOk(r) ? ok(fn(r.value)) : r;
}

export function mapErr<T, E, F>(r: Result<T, E>, fn: (e: E) => F): Result<T, F> {
  return isErr(r) ? err(fn(r.error)) : r;
}

export function andThen<T, U, E>(r: Result<T, E>, fn: (v: T) => Result<U, E>): Result<U, E> {
  return isOk(r) ? fn(r.value) : r;
}

export function unwrapOr<T, E>(r: Result<T, E>, fallback: T): T {
  return isOk(r) ? r.value : fallback;
}

export function match<T, E, R>(
  r: Result<T, E>,
  arms: { ok: (v: T) => R; err: (e: E) => R }
): R {
  return isOk(r) ? arms.ok(r.value) : arms.err(r.error);
}

export async function fromPromise<T>(p: Promise<T>): Promise<Result<T, Error>> {
  try {
    return ok(await p);
  } catch (e) {
    return err(e instanceof Error ? e : new Error(String(e)));
  }
}

export function tryCatch<T>(fn: () => T): Result<T, Error> {
  try {
    return ok(fn());
  } catch (e) {
    return err(e instanceof Error ? e : new Error(String(e)));
  }
}
